if (!customElements.get('td-before-after')) {
  class TDBeforeAfter extends HTMLElement {
    static EDGE_OFFSET = 24;

    static SELECTORS = {
      slider: '[data-td-before-after-slider]',
      slide: '[data-td-before-after-slide]',
      viewport: '[data-td-embla-viewport]',
    };

    constructor() {
      super();
      this.emblaApi = null;
      this.handleEmblaInit = this.handleEmblaInit.bind(this);
      this.handleBlockSelect = this.handleBlockSelect.bind(this);
      this.handleResize = this.handleResize.bind(this);
    }

    connectedCallback() {
      this.sliderElement = this.querySelector(TDBeforeAfter.SELECTORS.slider);
      this.slideElements = Array.from(this.querySelectorAll(TDBeforeAfter.SELECTORS.slide));
      this.viewportElement = this.querySelector(TDBeforeAfter.SELECTORS.viewport);

      if (!(this.sliderElement instanceof HTMLElement)) {
        return;
      }

      this.sliderElement.addEventListener('embla:init', this.handleEmblaInit);

      if (this.sliderElement.emblaApi) {
        this.emblaApi = this.sliderElement.emblaApi;
        this.applyEdgeAlign();
      }

      document.addEventListener('shopify:block:select', this.handleBlockSelect);
      window.addEventListener('resize', this.handleResize, { passive: true });

      if (typeof ResizeObserver === 'function' && this.viewportElement instanceof HTMLElement) {
        this.resizeObserver = new ResizeObserver(() => {
          this.updateSliderState();
        });
        this.resizeObserver.observe(this.viewportElement);
      }

      this.updateSliderState();
    }

    disconnectedCallback() {
      this.sliderElement?.removeEventListener('embla:init', this.handleEmblaInit);
      document.removeEventListener('shopify:block:select', this.handleBlockSelect);
      window.removeEventListener('resize', this.handleResize);
      this.resizeObserver?.disconnect();
      this.resizeObserver = null;
      this.emblaApi = null;
    }

    handleEmblaInit(event) {
      this.emblaApi = event?.detail?.api || this.sliderElement?.emblaApi || null;
      this.applyEdgeAlign();
      this.updateSliderState();
    }

    // Inner slides center; the first/last card edges stop 24px from the viewport.
    applyEdgeAlign() {
      if (!this.emblaApi || !this.slideElements.length) {
        return;
      }

      const align = (viewSize, snapSize, index) => {
        const lastIndex = this.slideElements.length - 1;
        if (index !== 0 && index !== lastIndex) {
          return (viewSize - snapSize) / 2;
        }

        const gap = parseFloat(getComputedStyle(this.slideElements[0]).paddingInlineStart) || 0;

        if (index === 0) {
          return TDBeforeAfter.EDGE_OFFSET;
        }
        return viewSize - TDBeforeAfter.EDGE_OFFSET + gap - snapSize;
      };

      this.emblaApi.reInit({ align });
    }

    handleBlockSelect(event) {
      const blockId = event.detail?.blockId;
      const slide = this.getSlideByBlockId(blockId);
      if (!(slide instanceof HTMLElement)) {
        return;
      }

      if (this.dataset.tdSliderActive == 'false') {
        return;
      }

      if (!this.emblaApi) {
        this.emblaApi = this.sliderElement?.emblaApi || null;
      }

      if (!this.emblaApi) {
        return;
      }

      const slideIndex = this.slideElements.indexOf(slide);
      if (slideIndex >= 0) {
        this.emblaApi.scrollTo(slideIndex, true);
      }
    }

    getSlideByBlockId(blockId) {
      if (!blockId) {
        return null;
      }

      const escapedBlockId = typeof CSS !== 'undefined' && typeof CSS.escape === 'function' ? CSS.escape(blockId) : blockId;
      return this.querySelector(`[data-td-block-id="${escapedBlockId}"]`);
    }

    handleResize() {
      if (typeof this.emblaApi?.reInit === 'function') {
        this.emblaApi.reInit();
      }

      this.updateSliderState();
    }

    updateSliderState() {
      if (!(this.viewportElement instanceof HTMLElement)) {
        return;
      }

      const hasOverflow = this.viewportElement.scrollWidth - this.viewportElement.clientWidth > 1;
      this.dataset.tdSliderActive = hasOverflow ? 'true' : 'false';
    }
  }

  customElements.define('td-before-after', TDBeforeAfter);
}
